<style lang="postcss">
	.timepicker-cover {
		display: flex;
		overflow: hidden;
	}
	.timepicker-24 {
		width: 100%;
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		overflow: auto;
	}
	.time-divider {
		grid-column: 1 / -1;
		margin: 0.5em 0.65rem;
		height: 1px;
		width: auto;
		border-radius: 2px;
		background-color: currentColor;
		opacity: 0.1;
	}
	.atom {
		width: 3.75rem;
		cursor: pointer;
		padding: 0.2rem 0.65rem;
		border-radius: 0.125rem;
		display: flex;
		align-items: center;
		justify-content: center;
		letter-spacing: 0.025em;
		font-weight: 475;
		opacity: 0.35;
	}
	.atom:hover,
	.atom.active {
		opacity: 1;
	}
	.timepicker-item {
		display: flex;
		flex-direction: column;
		overflow-y: auto;
		max-height: 18rem;
		padding-right: 0.5rem;
		min-height: 6rem;
	}
	.timepicker-icon {
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-property: color, background-color, border-color,
			text-decoration-color, fill, stroke;
		transition-duration: 150ms;
		position: absolute;
		width: 2.5rem;
		height: 2.5rem;
		transition: fill 0.2s;
	}
	.timepicker-sun {
		z-index: -1;
		position: absolute;
		right: 0.75rem;
		top: 100%;
		margin-top: -1.25rem;
		display: flex;
		height: 2.5rem;
		width: 2.5rem;
		align-items: center;
		justify-content: center;
		opacity: 0.8;
		will-change: transform;
	}
</style>

<script lang="ts">
	import { getContext } from "svelte";
	import { portal } from "design/helpers/svelte-portal";
	import FieldError from "$form/Fields/Shared/FieldError.svelte";
	import IMask from "imask";
	import { onMount, onDestroy } from "svelte";
	import { BROWSER } from "esm-env";
	import { createEventDispatcher } from "svelte";
	import {
		autoUpdate,
		computePosition,
		flip,
		shift,
		offset
	} from "@floating-ui/dom";
	import { tweened } from "svelte/motion";
	import { slide } from "$lib/transitions/slide";

	import Label from "$form/Fields/Shared/Label.svelte";
	import Description from "$form/Fields/Shared/Description.svelte";
	import Input from "$form/Fields/Shared/Input.svelte";
	import { clickOutside } from "$lib/actions";
	import dayjs from "dayjs";
	import customParseFormat from "dayjs/plugin/customParseFormat";
	dayjs.extend(customParseFormat);

	export let field: FieldDisplay & { type: "Time" };
	const styling = getContext<Styling>("styling");
	const device = getContext<string>("device");
	const iframe = getContext<boolean>("iframe");
	export let value: string;
	const suffixIcon: Embed = {
		key: "clock-icon",
		type: "inline_svg",
		content: `<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 256 256"><path fill="currentColor" d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24m0 192a88 88 0 1 1 88-88a88.1 88.1 0 0 1-88 88m64-88a8 8 0 0 1-8 8h-56a8 8 0 0 1-8-8V72a8 8 0 0 1 16 0v48h48a8 8 0 0 1 8 8" /></svg>`
	};

	let internalValue: string;
	let deep_shadow: boolean = false;

	const dispatch = createEventDispatcher();

	let inputRef: HTMLInputElement;
	let timeMask: ReturnType<typeof IMask> | undefined;
	let meridian: string = "AM";
	let instanceIsOpen: boolean = false;
	let timepickerRef: HTMLDivElement;
	let times: Array<{ hours: string; meridian: string; value: string }> = [];
	let sunRotation = tweened(0, { duration: 800 });
	let cleanup: CallbackFn | undefined;
	let min = field.settings.minComputed || "00:00";
	let max = field.settings.maxComputed || "23:59";
	let placeholder = styling.placeholders
		? field.labelComputed
		: field.settings.format12
			? "00:00"
			: "12:00 AM";
	let display_format = field.settings.format12 ? "hh:mm A" : "HH:mm";
	const format_12 = "hh:mm A";
	const format_24 = "HH:mm";
	$: display_format = field.settings.format12 ? format_12 : format_24;

	$: sunRotation.set(meridian === "AM" ? 0 : 36);
	$: min = field.settings.minComputed || "00:00";
	$: max = field.settings.maxComputed || "23:59";
	$: format12 = field.settings.format12 ?? true;
	$: {
		min, max;
		updateTimes();
	}

	$: {
		if (instanceIsOpen) {
			const am_times = times.filter((x) => x.meridian === "AM");
			const pm_times = times.filter((x) => x.meridian === "PM");
			if (am_times.length === 0) {
				meridian = "PM";
			} else if (pm_times.length === 0) {
				meridian = "AM";
			}
		}
		if (instanceIsOpen && timepickerRef && typeof cleanup === "undefined") {
			cleanup = autoUpdate(inputRef, timepickerRef, () => {
				update();
			});
		}
		if (!instanceIsOpen && typeof cleanup !== "undefined") {
			cleanup();
			cleanup = undefined;
		}
	}

	$: {
		if (typeof internalValue === "undefined" && value) {
			const day = dayjs(value, format_24, false);
			if (day.isValid()) {
				internalValue = day.format(display_format);
			}
		}
	}

	$: {
		// When the field is updated, update the value of the input
		if (timeMask && value) {
			const day = dayjs(value, format_24, true);
			const day2 = dayjs(value, format_12, true);
			if (day.isValid()) {
				inputRef.value = day.format(display_format);
				value = day.format(format_24);
				timeMask.updateValue();
			}
			if (day2.isValid()) {
				inputRef.value = day2.format(display_format);
				value = day2.format(format_24);
				timeMask.updateValue();
			}
		}
	}

	function updateTimes() {
		times = [];

		const difference = timeDifferenceInMinutes(min, max);
		const step = !isNaN(parseInt(field.settings.step, 10))
			? parseInt(field.settings.step, 10)
			: 30;
		const dayjsStart = dayjs(min, format_24);
		let i = 0;

		while (i <= difference) {
			const newDayjs = dayjsStart.add(i, "minute");
			times.push({
				hours: newDayjs.format("hh:mm"),
				meridian: newDayjs.format("A"),
				value: newDayjs.format(format_24)
			});
			i += step;
		}

		const is_value_valid = !value || times.some((x) => x.value === value);
		if (!is_value_valid) {
			value = "";
			internalValue = "";
		}
		if (times[0].value && styling.placeholders === false) {
			placeholder = dayjs(times[0].value, format_24).format(display_format);
		}
	}

	function timeDifferenceInMinutes(time1: any, time2: any) {
		const diffInMinutes = dayjs(time2, format_24).diff(
			dayjs(time1, format_24),
			"minute"
		);
		return Math.max(0, Math.min(diffInMinutes, 1440));
	}

	const open = () => {
		instanceIsOpen = true;
		requestAnimationFrame(() => {
			update();
		});
	};

	const close = () => {
		instanceIsOpen = false;
	};

	const keyboardEventListeners = (e: KeyboardEvent) => {
		if (e.key === "Tab") {
			close();
		}
	};

	const update = async () => {
		if (!timepickerRef) return;
		const { x, y } = await computePosition(inputRef, timepickerRef, {
			placement: "bottom-start",
			middleware: [offset(6), flip(), shift({ padding: 5 })]
		});
		if (window.innerHeight <= 360 && iframe === true) {
			deep_shadow = true;
			Object.assign(timepickerRef.style, {
				width: `calc(100% - 4rem)`,
				left: `50%`,
				top: `50%`,
				transform: `translate(-50%, -50%)`
			});
		} else {
			Object.assign(timepickerRef.style, {
				left: `${x}px`,
				top: `${y}px`
			});
		}
	};

	function isCrossover(time: any, nextTime: any) {
		if (!nextTime) return false;
		const time1 = dayjs(time.value, format_24);
		const time2 = dayjs(nextTime.value, format_24);
		return time1.format("A") !== time2.format("A");
	}

	onDestroy(() => {
		if (timeMask) timeMask.destroy();
		if (cleanup) cleanup();
		document.removeEventListener("keydown", keyboardEventListeners);
		if (inputRef) {
			inputRef.removeEventListener("focus", onFocus);
			inputRef.removeEventListener("blur", onBlur);
		}
		timeMask = undefined;
	});

	function onFocus() {
		open();
		dispatch("updateOther", {
			active: true
		});
	}

	// Switch meridian, and also try to change the timepicker value to the same time in the new meridian if it's valid
	function switchMeridian(newMeridian: string) {
		if (newMeridian === meridian) return;
		meridian = newMeridian;
		const newValue = dayjs(value, format_24)
			.set(
				"hour",
				(dayjs(value, format_24).hour() % 12) + (meridian === "AM" ? 0 : 12)
			)
			.format(format_24);
		if (times.some((x) => x.value === newValue)) {
			value = newValue;
			internalValue = dayjs(newValue, format_24).format(display_format);
		}
	}

	function onBlur() {
		dispatch("updateOther", {
			tainted: true
		});
		dispatch("updateOther", {
			active: false
		});
	}

	onMount(async () => {
		if (!BROWSER) return;
		document.addEventListener("keydown", keyboardEventListeners);
		inputRef.addEventListener("focus", onFocus);
		inputRef.addEventListener("blur", onBlur);
		timeMask = IMask(inputRef, {
			mask: format12 ? "HH:MM t" : "HH:MM",
			prepare: function (str: string) {
				return str.toUpperCase();
			},
			eager: true,
			lazy: true,
			overwrite: true,
			blocks: {
				HH: {
					mask: IMask.MaskedRange,
					placeholderChar: "0",
					from: 0,
					to: format12 ? 12 : 24,
					autofix: "pad"
				},
				MM: {
					mask: IMask.MaskedRange,
					placeholderChar: "0",
					from: 0,
					to: 60,
					autofix: "pad"
				},
				t: {
					mask: IMask.MaskedEnum,
					enum: ["AM", "PM"],
					autofix: "pad"
				}
			}
		});
		timeMask.on("accept", () => {
			if (timeMask) value = timeMask.value;
		});
	});
	function windowClick(e) {
		const ignore = [timepickerRef, inputRef];
		if (!instanceIsOpen) return;
		if (ignore.some((el) => el && el.contains(e.target))) return;
		close();
	}

	const styles = "font-variant-numeric: tabular-nums lining-nums";
</script>

<svelte:window on:mousedown="{windowClick}" />

<div
	class="field-label-cover"
	class:has-placeholder="{styling.placeholders !== false}">
	{#if field.labelHTMLComputed && field.settings.hideLabel !== true}
		{#if styling.placeholders === false}
			<Label html="{field.labelHTMLComputed}" />
		{/if}
		{#if field.descriptionHTMLComputed}
			<Description html="{field.descriptionHTMLComputed}" />
		{/if}
	{/if}
</div>

<div class="field-input-cover">
	<div style="display: flex; align-items: center">
		<Input
			suffixIcon="{suffixIcon}"
			bind:element="{inputRef}"
			bind:value="{internalValue}"
			name="{field.id}"
			label="{field.labelComputed}"
			placeholder="{placeholder}"
			style="letter-spacing: 0.025em; font-variant-numeric: tabular"
			type="text" />
		{#if instanceIsOpen}
			{#if deep_shadow}
				<div use:portal="{'body'}" class="deep-shadow-portal"></div>
			{/if}
			<div
				use:portal="{'body'}"
				bind:this="{timepickerRef}"
				class:shadow-dropdown-dark="{deep_shadow}"
				class:text-xl="{deep_shadow}"
				style="font-size: {deep_shadow
					? '1.15rem'
					: '.9rem'}; position: {deep_shadow ? 'fixed' : 'absolute'}"
				class="shadow-dropdown form-popup timepicker-cover">
				{#if format12 === true}
					<div class="timepicker-item">
						{#each times.filter((x) => x.meridian === meridian) as time}
							<button
								type="button"
								on:click="{() => {
									value = time.value;
									close();
								}}"
								transition:slide="{{ duration: 200 }}"
								class:active="{time.value === value}"
								class="atom"
								style="{styles}">
								{time.hours}
							</button>
						{:else}
							<div class="atom" style="{styles}"></div>
						{/each}
					</div>
					<div class="relative flex flex-col">
						<button
							type="button"
							class="atom"
							style="{styles}"
							class:active="{meridian === 'AM'}"
							on:click="{() => switchMeridian('AM')}">AM</button>
						<button
							type="button"
							class="atom"
							style="{styles}"
							class:active="{meridian === 'PM'}"
							on:click="{() => switchMeridian('PM')}">PM</button>
					</div>
					<div
						style="transform: translate3d(0, 0, 0) rotate({$sunRotation}deg);"
						class="timepicker-sun">
						<svg
							class="timepicker-icon"
							version="1.1"
							id="Layer_1"
							xmlns="http://www.w3.org/2000/svg"
							xmlns:xlink="http://www.w3.org/1999/xlink"
							viewBox="0 0 512 512"
							xml:space="preserve"
							style="transition-duration: 800ms; fill: {meridian === 'PM'
								? 'rgb(244 119 53)'
								: '#f0c417'};">
							<circle cx="256" cy="256" r="148.536"></circle>
							<path
								d="M256,512c-4.661,0-8.44-3.778-8.44-8.44v-65.266c0-4.662,3.779-8.44,8.44-8.44s8.44,3.778,8.44,8.44 v65.266C264.44,508.222,260.661,512,256,512z"
							></path>
							<path
								d="M401.519,464.72c-2.61,0-5.183-1.206-6.835-3.48l-38.363-52.801 c-2.74-3.771-1.904-9.048,1.867-11.788c3.772-2.741,9.049-1.904,11.788,1.867l38.363,52.801c2.74,3.771,1.904,9.048-1.867,11.788 C404.975,464.197,403.24,464.72,401.519,464.72z"
							></path>
							<path
								d="M491.446,340.941c-0.864,0-1.743-0.134-2.609-0.415l-62.071-20.168 c-4.432-1.44-6.86-6.201-5.418-10.634c1.44-4.434,6.201-6.859,10.634-5.418l62.071,20.168c4.432,1.44,6.86,6.201,5.418,10.634 C498.311,338.675,495.003,340.941,491.446,340.941z"
							></path>
							<path
								d="M429.371,208.109c-3.558,0-6.866-2.267-8.024-5.835c-1.44-4.432,0.986-9.193,5.418-10.634 l62.071-20.168c4.429-1.441,9.194,0.986,10.634,5.418c1.44,4.432-0.986,9.193-5.418,10.634l-62.071,20.168 C431.114,207.976,430.235,208.109,429.371,208.109z"
							></path>
							<path
								d="M363.143,116.961c-1.719,0-3.456-0.524-4.953-1.614c-3.771-2.74-4.607-8.018-1.867-11.788 l38.363-52.801c2.739-3.771,8.016-4.61,11.788-1.867c3.771,2.74,4.607,8.018,1.867,11.788l-38.363,52.801 C368.326,115.754,365.753,116.961,363.143,116.961z"
							></path>
							<path
								d="M256,82.145c-4.661,0-8.44-3.778-8.44-8.44V8.44c0-4.662,3.779-8.44,8.44-8.44s8.44,3.778,8.44,8.44 v65.266C264.44,78.368,260.661,82.145,256,82.145z"
							></path>
							<path
								d="M148.857,116.961c-2.61,0-5.183-1.206-6.835-3.48l-38.362-52.8c-2.74-3.771-1.904-9.048,1.867-11.788 c3.772-2.741,9.049-1.904,11.788,1.867l38.363,52.801c2.74,3.771,1.904,9.048-1.867,11.788 C152.313,116.438,150.576,116.961,148.857,116.961z"
							></path>
							<path
								d="M82.629,208.109c-0.864,0-1.743-0.134-2.61-0.415l-62.071-20.168 c-4.432-1.44-6.86-6.201-5.418-10.634c1.44-4.434,6.203-6.855,10.634-5.418l62.071,20.168c4.432,1.44,6.86,6.201,5.418,10.634 C89.495,205.843,86.186,208.109,82.629,208.109z"
							></path>
							<path
								d="M20.554,340.941c-3.558,0-6.866-2.267-8.024-5.835c-1.44-4.432,0.986-9.194,5.418-10.634 l62.071-20.168c4.43-1.44,9.193,0.985,10.634,5.418c1.44,4.432-0.986,9.194-5.418,10.634l-62.071,20.168 C22.297,340.807,21.418,340.941,20.554,340.941z"
							></path>
							<path
								d="M110.481,464.72c-1.719,0-3.456-0.524-4.953-1.614c-3.771-2.74-4.607-8.018-1.867-11.788 l38.363-52.801c2.739-3.771,8.018-4.609,11.788-1.867c3.771,2.74,4.607,8.018,1.867,11.788l-38.363,52.801 C115.664,463.513,113.09,464.72,110.481,464.72z"
							></path>
						</svg>
					</div>
				{:else}
					<div class="timepicker-24">
						{#each times as time, i}
							{@const is_crossover = isCrossover(time, times[i - 1] ?? null)}
							<button
								type="button"
								on:click="{() => {
									value = time.value;
									close();
								}}"
								transition:slide="{{ duration: 200 }}"
								class:active="{time.value === value}"
								class="atom"
								style="{styles}">
								{time.value}
							</button>
							{#if is_crossover}
								<div class="time-divider"></div>
							{/if}
						{/each}
					</div>
				{/if}
			</div>
		{/if}
	</div>
	<FieldError
		visible="{field.tainted}"
		valid="{field.valid}"
		errorNumber="{field.errorNumber}"
		errorType="{field.errorType}" />
</div>
